import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import QRCodeLabel from './QRCodeLabel';
import { printQrLabelBatch } from '../utils/qrLabel';

/**
 * The labels for records just created: one QR per code, each printable,
 * downloadable and copyable, and all of them printable in one go -- a plate
 * pair or a slant set goes onto the bench as one batch of tubes.
 */
export default function CreatedCodes({ codes, captions = {}, onDismiss }) {
  const { t } = useTranslation('common');
  const [blocked, setBlocked] = useState(false);
  const [urls, setUrls] = useState({});
  const list = (codes || []).filter(Boolean);
  const key = list.join('|');

  useEffect(() => {
    let live = true;
    Promise.all(key.split('|').filter(Boolean).map((code) =>
      QRCode.toDataURL(code, { width: 220, margin: 1 }).then((url) => [code, url]).catch(() => [code, ''])))
      .then((pairs) => { if (live) setUrls(Object.fromEntries(pairs)); });
    return () => { live = false; };
  }, [key]);

  if (!list.length) return null;
  const printAll = () => {
    const ok = printQrLabelBatch(list.map((code) => ({ dataUrl: urls[code],
      text: captions[code] ? `${code} · ${captions[code]}` : code })).filter((i) => i.dataUrl));
    setBlocked(ok === false);
  };

  return (
    <section data-testid="created-codes" role="status"
      style={{ border: '1px solid var(--c-success)', borderRadius: 8, padding: 12, margin: '12px 0', display: 'grid', gap: 10 }}>
      <strong>{t('createdCodes.heading', { count: list.length })}</strong>
      {list.map((code) => <QRCodeLabel key={code} value={code} caption={captions[code] || ''} />)}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {list.length > 1 && (
          <button type="button" onClick={printAll} data-testid="print-all" title={t('createdCodes.printAllTip')}
            disabled={Object.keys(urls).length < list.length}>
            {t('createdCodes.printAll')}
          </button>
        )}
        {onDismiss && (
          <button type="button" onClick={onDismiss} title={t('createdCodes.dismissTip')}>{t('createdCodes.dismiss')}</button>
        )}
      </div>
      {blocked && <p role="alert">{t('createdCodes.popupBlocked')}</p>}
    </section>
  );
}
